import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { LoginResponse, Company, UserData, SubscriptionDetails, SubscriptionUsage } from '@/types/auth';
import type { ApiError } from '@/api/auth';

interface AuthState {
  isAuthenticated: boolean;
  user: LoginResponse | null;
  loading: boolean;
  error: ApiError | null;
  tokens: {
    access: string | null;
    refresh: string | null;
  };
  companySetupComplete: boolean;
  verificationPending: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null,
  tokens: {
    access: null,
    refresh: null,
  },
  companySetupComplete: false,
  verificationPending: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Registration actions
    registerRequest: (state, action: PayloadAction<UserData>) => {
      state.loading = true;
      state.error = null;
    },
    registerSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.loading = false;
      state.user = action.payload;
      state.tokens = action.payload.tokens;
      state.verificationPending = true;
      state.error = null;
    },
    registerFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Login actions
    loginRequest: (state, action: PayloadAction<{ user_id: string; password: string }>) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.tokens = action.payload.tokens;
      state.loading = false;
      state.error = null;
      state.companySetupComplete = !!action.payload.company;
    },
    loginFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Verification actions
    verifyRequest: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    verifySuccess: (state) => {
      state.loading = false;
      state.verificationPending = false;
      state.error = null;
    },
    verifyFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Company setup actions
    setupCompanyRequest: (state, action: PayloadAction<Company>) => {
      state.loading = true;
      state.error = null;
    },
    setupCompanySuccess: (state, action: PayloadAction<Company>) => {
      state.isAuthenticated = true;
      state.loading = false;
      state.companySetupComplete = true;
      if (state.user) {
        state.user.company = action.payload;
      }
      state.error = null;
    },
    setupCompanyFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Logout action
    logout: (state) => {
      return initialState;
    },

    // Token management
    setTokens: (state, action: PayloadAction<{ access: string; refresh: string }>) => {
      state.tokens = action.payload;
    },
    setCompanySetupComplete: (state, action: PayloadAction<boolean>) => {
      state.companySetupComplete = action.payload;
    },

    // Add new action for subscription updates
    updateSubscriptionInfo: (
      state,
      action: PayloadAction<{
        subscription: SubscriptionDetails;
        subscription_usage: SubscriptionUsage;
      }>
    ) => {
      if (state.user) {
        state.user.subscription = action.payload.subscription;
        state.user.subscription_usage = action.payload.subscription_usage;
      }
    },
  },
});

export const { 
  registerRequest,
  registerSuccess,
  registerFailure,
  loginRequest,
  loginSuccess,
  loginFailure,
  verifyRequest,
  verifySuccess,
  verifyFailure,
  setupCompanyRequest,
  setupCompanySuccess,
  setupCompanyFailure,
  logout,
  setTokens,
  setCompanySetupComplete,
  updateSubscriptionInfo,
} = authSlice.actions;

export default authSlice.reducer;